<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("store_transfer_list") }}</h4>
          <h6 v-if="pendingInbound" class="text-warning mb-0">
            <i class="ti ti-bell me-1"></i>
            {{ $t("pending_inbound_transfers", { count: pendingInbound }) }}
          </h6>
        </div>
      </div>

      <div v-if="can('inventory.store_transfer', 'W')" class="page-btn">
        <router-link to="/store/transfer" class="btn btn-added">
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t("new_store_transfer") }}
        </router-link>
      </div>
    </div>

    <ul class="nav nav-tabs mb-3 flex-wrap gap-1">
      <li v-for="tab in tabs" :key="tab.key" class="nav-item">
        <button
          type="button"
          class="nav-link"
          :class="{ active: activeTab === tab.key }"
          @click="setTab(tab.key)"
        >
          {{ tab.label }}
          <span v-if="tab.badge" class="badge bg-warning text-dark ms-1">{{ tab.badge }}</span>
        </button>
      </li>
    </ul>

    <ThemeDataTable
      ref="transferTable"
      table-id="StoreTransferTable"
      :fetch-data="fetchTransfersPage"
      :default-order-column="1"
      default-order-dir="desc"
      :empty-title="$t('no_data_found')"
      :empty-hint="$t('try_adjusting_filters')"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("transfer_voucher_no") }}</th>
            <th>{{ $t("transfer_voucher_date") }}</th>
            <th class="no-sort">{{ $t("from_store") }}</th>
            <th class="no-sort">{{ $t("to_store") }}</th>
            <th class="no-sort">{{ $t("item_name") }}</th>
            <th>{{ $t("transfer_qty") }}</th>
            <th class="no-sort">{{ $t("status") }}</th>
            <th class="no-sort text-end">{{ $t("action") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="`${row.transfer_id || 'L'}-${row.line_id || row.id}`">
            <td>{{ row.transfer_voucher }}</td>
            <td>{{ formatVoucherDate(row.transfer_voucher_date) }}</td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <span
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-building-store"></i>
                  </span>
                </span>
                <span>{{ row.from_store }}</span>
              </div>
            </td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <span
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-building-warehouse"></i>
                  </span>
                </span>
                <span>{{ row.to_store }}</span>
              </div>
            </td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <span
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-package"></i>
                  </span>
                </span>
                <span>{{ row.item_name }}</span>
              </div>
            </td>
            <td>{{ row.transfer_qty }}</td>
            <td class="text-nowrap">
              <span class="badge" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
              <button
                v-if="row.status === 'rejected' && row.response_note"
                type="button"
                class="btn btn-sm btn-outline-secondary ms-2"
                @click="openViewNoteModal(row)"
              >
                {{ $t("view_reject_note") }}
              </button>
            </td>
            <td class="action-table-data text-end text-nowrap">
              <div class="transfer-row-actions">
                <div class="transfer-row-actions__main">
                  <template v-if="row.can_accept && can('inventory.store_transfer', 'W')">
                    <button
                      type="button"
                      class="btn btn-sm btn-success"
                      :disabled="actingKey === lineKey(row)"
                      @click="respond(row, 'accept')"
                    >
                      {{ $t("accept_transfer") }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      :disabled="actingKey === lineKey(row)"
                      @click="openRejectModal(row)"
                    >
                      {{ $t("reject_transfer") }}
                    </button>
                  </template>
                  <button
                    v-else-if="row.can_cancel && can('inventory.store_transfer', 'W')"
                    type="button"
                    class="btn btn-sm btn-outline-secondary transfer-cancel-btn"
                    :disabled="actingKey === lineKey(row)"
                    @click="respond(row, 'cancel')"
                  >
                    {{ $t("cancel_transfer") }}
                  </button>
                  <span
                    v-else-if="!showPdf(row)"
                    class="text-muted"
                  >—</span>
                </div>
                <div class="transfer-row-actions__pdf">
                  <button
                    v-if="showPdf(row)"
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :title="$t('transfer_pdf')"
                    :disabled="documentBusyKey === documentKey(row)"
                    @click="downloadTransferPdf(row)"
                  >
                    <img src="/assets/img/icons/pdf.svg" alt="PDF" width="14" height="14" class="me-1" />
                    {{ $t("transfer_pdf") }}
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>

    <div
      id="reject-transfer-modal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="reject-transfer-modal-label"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="reject-transfer-modal-label" class="modal-title">{{ $t("reject_transfer") }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('close')"></button>
          </div>
          <div class="modal-body">
            <p class="mb-1">{{ $t("confirm_reject_transfer_item") }}</p>
            <p v-if="rejectRow" class="small text-muted mb-2">
              {{ rejectRow.item_name }} · {{ rejectRow.transfer_qty }}
            </p>
            <label class="form-label">{{ $t("reject_note") }} <span class="text-danger">*</span></label>
            <textarea
              v-model="rejectNote"
              class="form-control"
              rows="3"
              :placeholder="$t('reject_note_placeholder')"
              maxlength="500"
            ></textarea>
            <div v-if="rejectNoteError" class="text-danger small mt-1">{{ rejectNoteError }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t("close") }}</button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="actingKey === lineKey(rejectRow)"
              @click="confirmReject"
            >
              {{ $t("reject_transfer") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      id="view-reject-note-modal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="view-reject-note-modal-label"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="view-reject-note-modal-label" class="modal-title">{{ $t("reject_note") }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('close')"></button>
          </div>
          <div class="modal-body">
            <p v-if="viewNoteRow" class="small text-muted mb-2">
              {{ viewNoteRow.transfer_voucher }}
              · {{ viewNoteRow.item_name }}
              · {{ viewNoteRow.from_store }} → {{ viewNoteRow.to_store }}
            </p>
            <p v-if="viewNoteRow?.direction === 'out'" class="small text-muted mb-2">
              {{ $t("reject_note_from_receiver", { store: viewNoteRow.to_store }) }}
            </p>
            <p class="mb-0" style="white-space: pre-wrap;">{{ viewNoteRow?.response_note }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t("close") }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { can as canAccess } from "@/helpers/permission";
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import { useToast } from 'vue-toastification';

const SORT_KEYS = {
  0: (row) => row.transfer_voucher || '',
  1: (row) => row.transfer_voucher_date || '',
  5: (row) => Number(row.transfer_qty) || 0,
};

export default {
  components: {
    ThemeDataTable,
  },

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      transferRows: null,
      loadingRows: null,
      pendingInbound: 0,
      pendingOutbound: 0,
      activeTab: 'all',
      actingKey: null,
      rejectRow: null,
      rejectNote: '',
      rejectNoteError: '',
      viewNoteRow: null,
      documentBusyKey: null,
    };
  },

  computed: {
    tabs() {
      return [
        { key: 'all', label: this.$t('transfer_tab_all'), badge: null },
        {
          key: 'pending_in',
          label: this.$t('transfer_tab_pending_in'),
          badge: this.pendingInbound || null,
        },
        {
          key: 'pending_out',
          label: this.$t('transfer_tab_pending_out'),
          badge: this.pendingOutbound || null,
        },
        { key: 'completed', label: this.$t('transfer_tab_completed'), badge: null },
        { key: 'rejected', label: this.$t('transfer_tab_rejected'), badge: null },
      ];
    },
  },

  methods: {
    formatVoucherDate(value) {
      if (!value) return '—';
      const raw = String(value).slice(0, 10);
      const [year, month, day] = raw.split('-');
      return year && month && day ? `${day}-${month}-${year}` : value;
    },

    documentKey(row) {
      return row.transfer_id ? `t-${row.transfer_id}` : `v-${row.transfer_voucher}`;
    },

    lineKey(row) {
      if (!row) return null;
      return `${this.documentKey(row)}-L${row.line_id || row.id}`;
    },

    /** PDF only for sender pending lines; once per voucher. */
    showPdf(row) {
      if (!row?.can_pdf || row.status !== 'pending') return false;
      const first = (this.transferRows || []).find(
        (r) =>
          this.documentKey(r) === this.documentKey(row)
          && r.can_pdf
          && r.status === 'pending',
      );
      return first && (first.line_id || first.id) === (row.line_id || row.id);
    },

    tabFilter(rows) {
      if (this.activeTab === 'pending_in') {
        return rows.filter((r) => r.status === 'pending' && r.direction === 'in');
      }
      if (this.activeTab === 'pending_out') {
        return rows.filter((r) => r.status === 'pending' && r.direction === 'out');
      }
      if (this.activeTab === 'completed') {
        return rows.filter((r) => r.status === 'accepted');
      }
      if (this.activeTab === 'rejected') {
        return rows.filter((r) => r.status === 'rejected');
      }
      return rows;
    },

    loadRows(force = false) {
      if (force) {
        this.loadingRows = null;
        this.transferRows = null;
      }
      if (!this.loadingRows) {
        this.loadingRows = this.$axios.get(apiUrls.store_transfer.get)
          .then((response) => {
            this.transferRows = response.data?.results || [];
            this.pendingInbound = response.data?.pending_inbound || 0;
            this.pendingOutbound = response.data?.pending_outbound || 0;
          })
          .catch((error) => {
            this.loadingRows = null;
            this.transferRows = [];
            this.pendingInbound = 0;
            this.pendingOutbound = 0;
            throw error;
          });
      }
      return this.loadingRows;
    },

    async fetchTransfersPage({ start, length, search, orderColumn, orderDir }) {
      await this.loadRows();
      const all = this.transferRows || [];
      let rows = this.tabFilter(all);

      const term = (search || '').toLowerCase();
      if (term) {
        rows = rows.filter((row) =>
          [
            row.transfer_voucher,
            row.transfer_voucher_date,
            row.from_store,
            row.to_store,
            row.item_name,
            row.status,
          ].some((value) => String(value ?? '').toLowerCase().includes(term)),
        );
      }

      const key = SORT_KEYS[orderColumn] || SORT_KEYS[1];
      const dir = orderDir === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const x = key(a);
        const y = key(b);
        const cmp = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
        return cmp * dir || ((b.id || 0) - (a.id || 0));
      });

      return {
        data: rows.slice(start, start + length),
        recordsTotal: all.length,
        recordsFiltered: rows.length,
      };
    },

    async fetchTransferPdfBlob(row) {
      const params = {};
      if (row.transfer_id) params.transfer_id = row.transfer_id;
      else if (row.transfer_voucher) params.voucher_no = row.transfer_voucher;
      else throw new Error('missing_transfer');
      const response = await this.$axios.get(apiUrls.store_transfer.document, {
        params,
        responseType: 'blob',
      });
      return response.data;
    },

    async downloadTransferPdf(row) {
      this.documentBusyKey = this.documentKey(row);
      try {
        const blob = await this.fetchTransferPdfBlob(row);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `store_transfer_${row.transfer_voucher || row.transfer_id || 'slip'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error(error);
        this.toast.error(this.$t('transfer_document_failed'));
      } finally {
        this.documentBusyKey = null;
      }
    },

    setTab(key) {
      if (this.activeTab === key) return;
      this.activeTab = key;
      this.$refs.transferTable?.reload?.();
    },

    statusLabel(status) {
      const map = {
        pending: this.$t('transfer_status_pending'),
        accepted: this.$t('transfer_status_accepted'),
        rejected: this.$t('transfer_status_rejected'),
        cancelled: this.$t('transfer_status_cancelled'),
      };
      return map[status] || status;
    },

    statusClass(status) {
      return {
        pending: 'bg-warning text-dark',
        accepted: 'bg-success',
        rejected: 'bg-danger',
        cancelled: 'bg-secondary',
      }[status] || 'bg-light text-dark';
    },

    showModal(id) {
      this.$nextTick(() => {
        const el = document.getElementById(id);
        if (el && window.bootstrap?.Modal) {
          window.bootstrap.Modal.getOrCreateInstance(el).show();
        }
      });
    },

    hideModal(id) {
      const el = document.getElementById(id);
      if (el && window.bootstrap?.Modal) {
        window.bootstrap.Modal.getOrCreateInstance(el).hide();
      }
    },

    openRejectModal(row) {
      this.rejectRow = row;
      this.rejectNote = '';
      this.rejectNoteError = '';
      this.showModal('reject-transfer-modal');
    },

    closeRejectModal() {
      this.hideModal('reject-transfer-modal');
      this.rejectRow = null;
      this.rejectNote = '';
      this.rejectNoteError = '';
    },

    openViewNoteModal(row) {
      this.viewNoteRow = row;
      this.showModal('view-reject-note-modal');
    },

    async confirmReject() {
      const note = (this.rejectNote || '').trim();
      if (!note) {
        this.rejectNoteError = this.$t('reject_note_required');
        return;
      }
      this.rejectNoteError = '';
      await this.respond(this.rejectRow, 'reject', note);
      this.closeRejectModal();
    },

    async respond(row, action, note = '') {
      if (!row?.transfer_id) return;
      if (action !== 'reject') {
        const confirmKey = {
          accept: 'confirm_accept_transfer_item',
          cancel: 'confirm_cancel_transfer_item',
        }[action];
        if (confirmKey && !window.confirm(this.$t(confirmKey))) return;
      }

      this.actingKey = this.lineKey(row);
      try {
        const url = apiUrls.store_transfer.respond
          .replace(':id', row.transfer_id)
          .replace(':action', action);
        const payload = {
          line_id: row.line_id || row.id,
        };
        if (note) payload.note = note;
        const response = await this.$axios.post(url, payload);
        this.toast.success(response.data?.message || this.$t('transfer_updated'));
        await this.loadRows(true);
        this.$refs.transferTable?.reload?.();
      } catch (error) {
        const msg = error?.response?.data?.error || this.$t('transfer_action_failed');
        this.toast.error(msg);
      } finally {
        this.actingKey = null;
      }
    },

    can(code, requiredLevel) {
      return canAccess(code, requiredLevel);
    },
  },
};
</script>

<style scoped>
:deep(td.action-table-data) {
  white-space: nowrap;
}
.transfer-row-actions {
  display: inline-grid;
  grid-template-columns: minmax(5.5rem, max-content) 4.75rem;
  column-gap: 0.25rem;
  justify-content: end;
  align-items: center;
  white-space: nowrap;
}
.transfer-row-actions__main {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.25rem;
  min-height: 31px;
}
.transfer-row-actions__pdf {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-height: 31px;
}
.transfer-cancel-btn {
  width: 5.25rem;
}
</style>
